import React, { useState } from "react";
import { Plus, Trash2, Copy, ChevronUp, ChevronDown, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/format";

const QTYPES = ["single_choice", "multiple_choice", "yes_no", "dropdown", "number", "currency",
  "text", "long_text", "slider", "measurement"];

export function Builder({ categories, projectTypes, questions, activeCat, setActiveCat, reload }) {
  const cat = categories.find((c) => c.id === activeCat) || categories[0];
  const types = projectTypes.filter((t) => t.category_id === cat?.id).sort((a, b) => a.order - b.order);
  const catQuestions = questions.filter((q) => q.category_id === cat?.id).sort((a, b) => a.order - b.order);
  const [editing, setEditing] = useState(null);

  const saveBasePrice = async (t, price) => {
    await api.put(`/admin/project-types/${t.id}`, { base_price: price });
    toast.success("Base price updated");
    reload();
  };
  const addType = async () => {
    const name = prompt("New project type name?");
    if (!name) return;
    await api.post("/admin/project-types", { category_id: cat.id, name, base_price: 0, order: types.length });
    reload();
  };
  const delType = async (t) => { if (window.confirm(`Delete ${t.name}?`)) { await api.delete(`/admin/project-types/${t.id}`); reload(); } };

  const addCategory = async () => {
    const name = prompt("New quote category name? (e.g. Garden Room)");
    if (!name) return;
    const slug = name.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    const description = prompt("Short description shown on the landing card?") || `Get an estimated cost for your ${name.toLowerCase()} project.`;
    const { data } = await api.post("/admin/categories", { name, slug, id: slug, description, order: categories.length });
    toast.success("Category created — now add project types and questions");
    await reload();
    setActiveCat(data.category.id);
  };

  const toggleActive = async (q) => { await api.put(`/admin/questions/${q.id}`, { active: !q.active }); reload(); };
  const move = async (q, dir) => {
    const idx = catQuestions.indexOf(q);
    const swap = catQuestions[idx + dir];
    if (!swap) return;
    await api.post("/admin/questions/reorder", { category_id: cat.id,
      orders: [{ id: q.id, order: swap.order }, { id: swap.id, order: q.order }] });
    reload();
  };
  const dup = async (q) => { await api.post(`/admin/questions/${q.id}/duplicate`); toast.success("Duplicated"); reload(); };
  const del = async (q) => { if (window.confirm(`Delete "${q.title}"?`)) { await api.delete(`/admin/questions/${q.id}`); reload(); } };
  const addQuestion = () => setEditing({
    category_id: cat.id, title: "New question", type: "single_choice", section: "General",
    required: true, active: true, order: catQuestions.length, options: [], conditions: [],
    condition_logic: "all", pricing: { mode: "none" }, _new: true,
  });

  if (!cat) return null;
  return (
    <div>
      {/* Category tabs */}
      <div className="mb-6 flex flex-wrap items-center gap-2">
        {categories.map((c) => (
          <button key={c.id} onClick={() => setActiveCat(c.id)}
            className={`rounded-lg px-4 py-2 text-sm font-semibold ${c.id === cat.id ? "bg-amber-600 text-slate-950" : "border border-slate-700 text-slate-300"}`}
            data-testid={`builder-cat-${c.id}`}>{c.name}</button>
        ))}
        <button onClick={addCategory} className="flex items-center gap-1.5 rounded-lg border border-dashed border-amber-500/50 px-4 py-2 text-sm font-semibold text-amber-400 hover:bg-amber-500/10" data-testid="builder-add-category-btn">
          <Plus className="h-4 w-4" /> New category
        </button>
      </div>

      {/* Project types / base prices */}
      <div className="mb-8 rounded-2xl border border-slate-800 bg-[#151820] p-5">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-heading text-lg font-semibold">Project types &amp; base prices</h3>
          <button onClick={addType} className="flex items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-bold text-slate-950" data-testid="builder-add-type-btn"><Plus className="h-3.5 w-3.5" /> Add type</button>
        </div>
        <div className="space-y-2">
          {types.map((t) => (
            <div key={t.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-800 bg-[#0d0f13] px-4 py-3">
              <span className="font-medium">{t.name}</span>
              <div className="flex items-center gap-2">
                <span className="text-slate-500">£</span>
                <input type="number" defaultValue={t.base_price} onBlur={(e) => { const v = Number(e.target.value); if (v !== t.base_price) saveBasePrice(t, v); }}
                  className="w-28 rounded-md border border-slate-700 bg-[#151820] px-3 py-1.5 text-right font-mono text-sm outline-none focus:border-amber-500" data-testid={`base-price-${t.id}`} />
                <button onClick={() => delType(t)} className="text-slate-500 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Questions */}
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-heading text-lg font-semibold">Questions ({catQuestions.length})</h3>
        <button onClick={addQuestion} className="flex items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-bold text-slate-950" data-testid="admin-questionnaire-builder-add-btn"><Plus className="h-4 w-4" /> Add question</button>
      </div>
      <div className="space-y-2">
        {catQuestions.map((q, i) => (
          <div key={q.id} className={`rounded-xl border bg-[#151820] p-4 ${q.active ? "border-slate-800" : "border-slate-800/50 opacity-60"}`} data-testid={`builder-question-${q.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="rounded bg-slate-800 px-1.5 py-0.5 text-[10px] font-mono text-slate-400">{q.section}</span>
                  <span className="rounded bg-slate-800 px-1.5 py-0.5 text-[10px] font-mono text-amber-400">{q.type}</span>
                  {q.conditions?.length > 0 && <span className="rounded bg-blue-500/10 px-1.5 py-0.5 text-[10px] font-mono text-blue-300">conditional</span>}
                </div>
                <p className="mt-1.5 font-medium">{q.title}</p>
                {q.options?.length > 0 && <p className="mt-0.5 truncate text-xs text-slate-500">{q.options.map((o) => o.label).join(", ")}</p>}
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button onClick={() => move(q, -1)} disabled={i === 0} className="rounded p-1.5 text-slate-500 hover:text-slate-200 disabled:opacity-30"><ChevronUp className="h-4 w-4" /></button>
                <button onClick={() => move(q, 1)} disabled={i === catQuestions.length - 1} className="rounded p-1.5 text-slate-500 hover:text-slate-200 disabled:opacity-30"><ChevronDown className="h-4 w-4" /></button>
                <button onClick={() => toggleActive(q)} className={`rounded px-2 py-1 text-[10px] font-bold ${q.active ? "bg-emerald-500/10 text-emerald-400" : "bg-slate-700 text-slate-400"}`} data-testid={`toggle-active-${q.id}`}>{q.active ? "ON" : "OFF"}</button>
                <button onClick={() => setEditing({ ...q })} className="rounded p-1.5 text-amber-400 hover:bg-amber-500/10" data-testid={`edit-question-${q.id}`}><Pencil className="h-4 w-4" /></button>
                <button onClick={() => dup(q)} className="rounded p-1.5 text-slate-500 hover:text-slate-200"><Copy className="h-4 w-4" /></button>
                <button onClick={() => del(q)} className="rounded p-1.5 text-slate-500 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && <QuestionModal q={editing} allQuestions={catQuestions} onClose={() => setEditing(null)} reload={reload} />}
    </div>
  );
}

function QuestionModal({ q, allQuestions, onClose, reload }) {
  const [form, setForm] = useState({
    ...q, options: q.options || [], conditions: q.conditions || [],
    pricing: q.pricing || { mode: "none" },
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setPricing = (k, v) => setForm((f) => ({ ...f, pricing: { ...f.pricing, [k]: v } }));
  const inp = "w-full rounded-lg border border-slate-700 bg-[#0d0f13] px-3 py-2 text-sm text-slate-100 outline-none focus:border-amber-500";

  const addOption = () => set("options", [...form.options, { id: `opt_${Date.now()}`, label: "New option", price_type: "add", price_value: 0, order: form.options.length }]);
  const updOption = (i, k, v) => { const o = [...form.options]; o[i] = { ...o[i], [k]: v }; set("options", o); };
  const delOption = (i) => set("options", form.options.filter((_, x) => x !== i));

  const addCond = () => set("conditions", [...form.conditions, { question_id: "", operator: "equals", value: "" }]);
  const updCond = (i, k, v) => { const c = [...form.conditions]; c[i] = { ...c[i], [k]: v }; set("conditions", c); };
  const delCond = (i) => set("conditions", form.conditions.filter((_, x) => x !== i));

  const hasOptions = ["single_choice", "multiple_choice", "yes_no", "dropdown"].includes(form.type);
  const isNumeric = ["number", "currency", "measurement", "slider"].includes(form.type);

  const save = async () => {
    const payload = { ...form }; delete payload._new; delete payload._id;
    try {
      if (q._new) await api.post("/admin/questions", payload);
      else await api.put(`/admin/questions/${q.id}`, payload);
      toast.success("Question saved");
      onClose(); reload();
    } catch (err) { toast.error("Save failed"); }
  };

  const condQuestion = allQuestions.filter((x) => x.id !== q.id);
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm" data-testid="question-modal">
      <div className="my-8 w-full max-w-2xl rounded-2xl border border-slate-700 bg-[#151820] p-6">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-xl font-bold">{q._new ? "New question" : "Edit question"}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200"><X /></button>
        </div>

        <div className="mt-5 space-y-4">
          <div>
            <label className="mb-1 block text-xs text-slate-400">Question title</label>
            <input value={form.title} onChange={(e) => set("title", e.target.value)} className={inp} data-testid="q-title-input" />
          </div>
          <div>
            <label className="mb-1 block text-xs text-slate-400">Description</label>
            <input value={form.description || ""} onChange={(e) => set("description", e.target.value)} className={inp} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="mb-1 block text-xs text-slate-400">Section</label><input value={form.section} onChange={(e) => set("section", e.target.value)} className={inp} /></div>
            <div><label className="mb-1 block text-xs text-slate-400">Type</label>
              <select value={form.type} onChange={(e) => set("type", e.target.value)} className={inp} data-testid="q-type-select">
                {QTYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="mb-1 block text-xs text-slate-400">Unit</label><input value={form.unit || ""} onChange={(e) => set("unit", e.target.value)} className={inp} placeholder="e.g. metres" /></div>
            <label className="flex items-center gap-2 pt-6 text-sm"><input type="checkbox" checked={form.required} onChange={(e) => set("required", e.target.checked)} className="h-4 w-4 accent-amber-500" /> Required</label>
            <label className="flex items-center gap-2 pt-6 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} className="h-4 w-4 accent-amber-500" /> Active</label>
          </div>

          {/* Options */}
          {hasOptions && (
            <div className="rounded-lg border border-slate-800 p-3">
              <div className="mb-2 flex items-center justify-between"><span className="text-sm font-semibold">Answer options &amp; pricing</span>
                <button onClick={addOption} className="rounded bg-slate-700 px-2 py-1 text-xs" data-testid="add-option-btn">+ Option</button></div>
              <div className="space-y-2">
                {form.options.map((o, i) => (
                  <div key={o.id} className="flex items-center gap-2">
                    <input value={o.label} onChange={(e) => updOption(i, "label", e.target.value)} className={`${inp} flex-1`} placeholder="Label" />
                    <select value={o.price_type} onChange={(e) => updOption(i, "price_type", e.target.value)} className={`${inp} w-32`}>
                      <option value="add">Add £</option><option value="fixed">Fixed £</option><option value="percentage">Percent %</option>
                    </select>
                    <input type="number" value={o.price_value} onChange={(e) => updOption(i, "price_value", Number(e.target.value))} className={`${inp} w-24`} />
                    <button onClick={() => delOption(i)} className="text-slate-500 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Numeric pricing */}
          {isNumeric && (
            <div className="rounded-lg border border-slate-800 p-3">
              <span className="text-sm font-semibold">Pricing behaviour</span>
              <div className="mt-2 grid grid-cols-2 gap-3">
                <select value={form.pricing.mode} onChange={(e) => setPricing("mode", e.target.value)} className={inp}>
                  <option value="none">No pricing</option><option value="per_unit">Per unit × value</option><option value="per_sqm">Per m² (× another field)</option>
                </select>
                <input type="number" value={form.pricing.price_per_unit || 0} onChange={(e) => setPricing("price_per_unit", Number(e.target.value))} className={inp} placeholder="£ per unit / m²" />
              </div>
              {form.pricing.mode === "per_sqm" && (
                <select value={form.pricing.area_multiplier_question_id || ""} onChange={(e) => setPricing("area_multiplier_question_id", e.target.value)} className={`${inp} mt-2`}>
                  <option value="">Multiply by… (select field)</option>
                  {condQuestion.map((x) => <option key={x.id} value={x.id}>{x.title}</option>)}
                </select>
              )}
            </div>
          )}

          {/* Conditions */}
          <div className="rounded-lg border border-slate-800 p-3">
            <div className="mb-2 flex items-center justify-between"><span className="text-sm font-semibold">Conditional visibility</span>
              <button onClick={addCond} className="rounded bg-slate-700 px-2 py-1 text-xs" data-testid="add-condition-btn">+ Condition</button></div>
            {form.conditions.length === 0 && <p className="text-xs text-slate-500">Always visible.</p>}
            <div className="space-y-2">
              {form.conditions.map((c, i) => {
                const target = allQuestions.find((x) => x.id === c.question_id);
                return (
                  <div key={i} className="flex flex-wrap items-center gap-2">
                    <select value={c.question_id} onChange={(e) => updCond(i, "question_id", e.target.value)} className={`${inp} flex-1`}>
                      <option value="">Select question…</option>
                      {condQuestion.map((x) => <option key={x.id} value={x.id}>{x.title}</option>)}
                    </select>
                    <select value={c.operator} onChange={(e) => updCond(i, "operator", e.target.value)} className={`${inp} w-28`}>
                      <option value="equals">equals</option><option value="not_equals">not equals</option>
                      <option value="greater_than">&gt;</option><option value="less_than">&lt;</option><option value="includes">includes</option>
                    </select>
                    {target?.options?.length ? (
                      <select value={c.value} onChange={(e) => updCond(i, "value", e.target.value)} className={`${inp} w-40`}>
                        <option value="">value…</option>
                        {target.options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                      </select>
                    ) : (
                      <input value={c.value} onChange={(e) => updCond(i, "value", e.target.value)} className={`${inp} w-40`} placeholder="value" />
                    )}
                    <button onClick={() => delCond(i)} className="text-slate-500 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="rounded-lg border border-slate-700 px-5 py-2.5 text-sm font-semibold">Cancel</button>
          <button onClick={save} className="rounded-lg bg-amber-600 px-5 py-2.5 text-sm font-bold text-slate-950" data-testid="q-save-btn">Save question</button>
        </div>
      </div>
    </div>
  );
}
